import { DashboardDateRangePicker } from 'App/dashboards/_components/DashboardDateRangePicker'
import { DashboardShell } from 'App/dashboards/_components/DashboardShell'
import { hasWaterUsageAssets } from 'App/dashboards/_utils/hasWaterUsageAssets'
import { useWaterDashboardPreferences } from 'App/dashboards/_utils/useWaterDashboardPreferences'
import { useZoneCrops } from 'App/dashboards/_utils/useZoneCrops'
import { FlowMeterRate } from 'App/dashboards/views/water/widgets/FlowMeterRate'
import { IrrigationActivityByPressureSensors } from 'App/dashboards/views/water/widgets/IrrigationActivityByPressureSensors'
import { IrrigationActivityByZone } from 'App/dashboards/views/water/widgets/IrrigationActivityByZone'
import { translate } from 'i18n/i18n'
import { useCallback, useMemo, useState } from 'react'

const WATER_USAGE_DASHBOARD_ID = 'water-usage'

export const WaterUsage = () => {
  const { options, selectedPropertyIds, onPropertyChange, dateFrom, dateTo, onDateRangeChange } =
    useWaterDashboardPreferences({ dashboardId: WATER_USAGE_DASHBOARD_ID, hasAssets: hasWaterUsageAssets })

  const propertyIds = useMemo(() => selectedPropertyIds.map(Number), [selectedPropertyIds])
  const zoneCrops = useZoneCrops(propertyIds)
  const showCrops = propertyIds.some((propertyId) => zoneCrops.multiCropPropertyIds.has(propertyId))
  // One refresh token shared by every widget, so the header Refresh refetches the whole dashboard.
  const [refreshToken, setRefreshToken] = useState(0)
  const onRefresh = useCallback(() => setRefreshToken((t) => t + 1), [])
  // "Last Updated" = the most recent successful fetch across the widgets (each reports via onLoaded).
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const reportLoaded = useCallback(
    (date: Date) => setLastUpdated((prev) => (!prev || date > prev ? date : prev)),
    [],
  )

  return (
    <DashboardShell
      title={translate.phrases.banyanApp('Water Usage')}
      lastUpdated={lastUpdated}
      onRefresh={onRefresh}
      propertyOptions={options}
      selectedPropertyIds={selectedPropertyIds}
      onPropertyChange={onPropertyChange}
      propertyPickerMode="multi"
      // Page-scrolled dashboard, so the header compacts from window scroll and the card titles,
      // property banners and their column headers pin beneath it as you read down.
      compactHeaderOnScroll
      headerActions={
        <DashboardDateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onDateRangeChange}
          isHistoricalOnly
        />
      }
    >
      <IrrigationActivityByZone
        propertyIds={propertyIds}
        dateFrom={dateFrom}
        dateTo={dateTo}
        zoneCrops={zoneCrops}
        refreshToken={refreshToken}
        onLoaded={reportLoaded}
      />

      {/* Flow Meter + Pressure Sensors sit side by side on desktop and wrap to stacked on iPad
          (flex-wrap). Both widgets always render (showing a no-access / no-data message when empty),
          so the row stays balanced. Pressure Sensors has six columns (seven with Crop), which need
          ~680px (~780px) before single words like "Application" or "Microspray" outgrow a column, so
          it gets the larger basis. */}
      <div css={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div css={{ flex: '1 1 500px', minWidth: 0 }}>
          <FlowMeterRate
            propertyIds={propertyIds}
            dateFrom={dateFrom}
            dateTo={dateTo}
            refreshToken={refreshToken}
            onLoaded={reportLoaded}
          />
        </div>

        <div css={{ flex: showCrops ? '1 1 780px' : '1 1 680px', minWidth: 0 }}>
          <IrrigationActivityByPressureSensors
            propertyIds={propertyIds}
            dateFrom={dateFrom}
            dateTo={dateTo}
            zoneCrops={zoneCrops}
            refreshToken={refreshToken}
            onLoaded={reportLoaded}
          />
        </div>
      </div>
    </DashboardShell>
  )
}
